/**
 * PronunciationResultPanel
 *
 * Shared verdict panel for any speaking-assessment surface (course-level
 * shadowing, custom-review sentence shadowing, single-word vocabulary
 * speaking). Both call sites send the same Azure result shape — overall
 * + accuracy + fluency + completeness + prosody scores plus a per-word
 * breakdown — and this component renders the panel exactly the same
 * way regardless of where it's used.
 *
 * Click-to-drill ("weak word" practice) is delegated upstream: the
 * component reports which word the learner clicked via `onWordPractice`,
 * and the parent owns the actual record / re-assess flow. That keeps
 * this layer presentational while the hosting page wires in its own
 * audio recorder and score-update plumbing.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import LoopyAvatar from '../loopy/LoopyAvatar';
import { stripWordPunctuation, isPunctuationOnly } from '../../utils/wordComparison';
import './PronunciationResultPanel.css';

const WEAK_WORD_THRESHOLD = 80;

export interface PhonemeScore {
  phoneme: string;
  accuracyScore: number;
}

export interface WordScore {
  word: string;
  accuracyScore: number;
  errorType: string; // None | Omission | Insertion | Mispronunciation | UnexpectedBreak | MissingBreak | Monotone
  phonemes?: PhonemeScore[];
  suggestion?: string;
}

export interface PronunciationAssessment {
  overallScore?: number;
  accuracyScore?: number;
  fluencyScore?: number;
  completenessScore?: number;
  prosodyScore?: number;
  words?: WordScore[];
  /** Heard transcript — surfaced as a side panel when it differs from
   *  the expected text. */
  transcribedText?: string;
  /** Expected reference text (sentence or word). */
  expectedText?: string;
}

export interface PronunciationResultPanelProps {
  result: PronunciationAssessment;
  /** Called when the learner taps a weak word — host wires it to a
   *  re-record / re-assess flow. Skip the prop to disable click. */
  onWordPractice?: (word: string, wordIndex: number) => void;
  /** Word indices the host already retried — show ✓ + green. */
  improvedWordIndices?: Set<number>;
  /** Suppress the click affordance while host is in mid-drill. */
  practiceLocked?: boolean;
}

const getScoreColor = (score: number): string => {
  if (score >= 80) return '#22c55e';
  if (score >= 60) return '#eab308';
  return '#ef4444';
};

const getWordStyle = (word: WordScore): React.CSSProperties => {
  if (word.errorType === 'Mispronunciation') {
    return { color: '#ef4444', fontWeight: 'bold' };
  }
  if (word.errorType === 'Omission') {
    return { color: '#f97316', textDecoration: 'line-through' };
  }
  if (word.accuracyScore >= 80) return { color: '#22c55e' };
  if (word.accuracyScore >= 60) return { color: '#eab308' };
  return { color: '#ef4444' };
};

const PronunciationResultPanel: React.FC<PronunciationResultPanelProps> = ({
  result,
  onWordPractice,
  improvedWordIndices,
  practiceLocked,
}) => {
  const { t } = useTranslation('learning');

  const overall = result.overallScore ?? result.accuracyScore;
  const accuracy = result.accuracyScore;
  const fluency = result.fluencyScore;
  const completeness = result.completenessScore;
  const prosody = result.prosodyScore;

  const showAxes = [accuracy, fluency, completeness, prosody].some(
    v => typeof v === 'number',
  );
  const renderableWords = (result.words ?? []).filter(w => {
    // Drop inserts — panel is reference-aligned.
    if (w.errorType === 'Insertion') return false;
    // Drop tokens that would render as nothing or just punctuation
    // (e.g. a lone "." that some diff tokenizers emit when the
    // sentence ends with a period).
    if (!w.word || !w.word.trim()) return false;
    if (isPunctuationOnly(w.word)) return false;
    return true;
  });
  const weakWords = renderableWords.filter(
    w => w.errorType !== 'Omission' && w.accuracyScore < WEAK_WORD_THRESHOLD,
  );

  return (
    <div className="pronunciation-result-panel">
      {(typeof overall === 'number' || showAxes) && (
        <div className="score-overview">
          {typeof overall === 'number' && (
            <div
              className="overall-score"
              style={{ borderColor: getScoreColor(overall) }}
            >
              <span
                className="score-value"
                style={{ color: getScoreColor(overall) }}
              >
                {Math.round(overall)}
              </span>
              <span className="score-label">
                {t('shadowing.scores.overall', { defaultValue: '總分' })}
              </span>
            </div>
          )}

          {showAxes && (
            <div className="score-details">
              {typeof accuracy === 'number' && (
                <ScoreBar
                  label={t('shadowing.scores.accuracy', { defaultValue: '準確度' })}
                  score={accuracy}
                />
              )}
              {typeof fluency === 'number' && (
                <ScoreBar
                  label={t('shadowing.scores.fluency', { defaultValue: '流暢度' })}
                  score={fluency}
                />
              )}
              {typeof completeness === 'number' && (
                <ScoreBar
                  label={t('shadowing.scores.completeness', { defaultValue: '完整度' })}
                  score={completeness}
                />
              )}
              {typeof prosody === 'number' && (
                <ScoreBar
                  label={t('shadowing.scores.prosody', { defaultValue: '語調' })}
                  score={prosody}
                  prosody
                />
              )}
            </div>
          )}
        </div>
      )}

      {renderableWords.length > 0 && (
        <div className="word-feedback">
          <h4>{t('shadowing.wordAnalysis.title', { defaultValue: '單字分析' })}</h4>
          <div className="words-list">
            {renderableWords.map((word, idx) => {
              const isWeak = word.accuracyScore < WEAK_WORD_THRESHOLD;
              // originalIdx — index in the full words array; needed for
              // host-side state keyed off the original position.
              const originalIdx = (result.words ?? []).indexOf(word);
              const hasImproved = !!improvedWordIndices?.has(originalIdx);
              const clickable = isWeak && !practiceLocked && !!onWordPractice;
              return (
                <span
                  key={idx}
                  className={`word-item ${isWeak && !practiceLocked ? 'weak-word' : ''} ${hasImproved ? 'improved' : ''}`}
                  style={getWordStyle(word)}
                  title={isWeak
                    ? t('shadowing.wordAnalysis.scoreTooltip', {
                        score: Math.round(word.accuracyScore),
                        defaultValue: `${Math.round(word.accuracyScore)} 分（點擊重練）`,
                      })
                    : t('shadowing.wordAnalysis.scoreOnly', {
                        score: Math.round(word.accuracyScore),
                        defaultValue: `${Math.round(word.accuracyScore)} 分`,
                      })}
                  onClick={clickable ? () => onWordPractice!(word.word, originalIdx) : undefined}
                >
                  {/* The diff tokenizer keeps trailing punctuation
                      ("ten.", "anecdotes."). Strip it for display so
                      the chip reads as the actual lexical word. The
                      raw `word.word` still flows back to the host on
                      click so the host owns its own cleanup before
                      sending to the per-word ASR reference. */}
                  {stripWordPunctuation(word.word) || word.word}
                  {hasImproved && <span className="improved-badge">✓</span>}
                </span>
              );
            })}
          </div>
          <div className="word-legend">
            <span className="legend-item">
              <span style={{ color: '#22c55e' }}>●</span>{' '}
              {t('shadowing.legend.good', { defaultValue: '發音正確' })}
            </span>
            <span className="legend-item">
              <span style={{ color: '#eab308' }}>●</span>{' '}
              {t('shadowing.legend.needsWork', { defaultValue: '需加強' })}
            </span>
            <span className="legend-item">
              <span style={{ color: '#ef4444' }}>●</span>{' '}
              {t('shadowing.legend.mispronounced', { defaultValue: '發音錯誤' })}
            </span>
            <span className="legend-item">
              <span style={{ color: '#f97316', textDecoration: 'line-through' }}>text</span>{' '}
              {t('shadowing.legend.omitted', { defaultValue: '漏唸' })}
            </span>
          </div>
          {weakWords.length > 0 && !practiceLocked && onWordPractice && (
            <div className="weak-words-hint">
              <LoopyAvatar
                size="xs"
                state="hint"
                emotion="point"
                ariaLabel={t('shadowing.weakWordsHint', { defaultValue: '點擊紅黃色單字可單獨重練' })}
              />
              {t('shadowing.weakWordsHint', { defaultValue: '點擊紅黃色單字可單獨重練' })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ScoreBar: React.FC<{ label: string; score: number; prosody?: boolean }> = ({
  label,
  score,
  prosody,
}) => (
  <div className="score-item">
    <span className="score-number" style={{ color: getScoreColor(score) }}>
      {Math.round(score)}
    </span>
    <div className="score-bar">
      <div
        className={`score-fill${prosody ? ' score-fill--prosody' : ''}`}
        style={{
          width: `${score}%`,
          backgroundColor: getScoreColor(score),
        }}
      />
    </div>
    <span className="score-name">{label}</span>
  </div>
);

export default PronunciationResultPanel;
